import React, { useMemo } from 'react';
import type {
  ColDef,
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getBanks, saveBatchBanks } from '../../api/banks';
import { useCreditRiskTypes } from '../../hooks/useCreditRiskTypes';
import { useLendingFeeCalcTypes } from '../../hooks/useLendingFeeCalcTypes';
import type { Bank } from '../../types/Bank';
import { t } from '../../i18n/t';

type CreditRiskField = 'creditRiskTypeId' | 'counterpartyCreditRiskTypeId';

const newBankDefaults = (): Partial<Bank> => ({
  bankId: 0,
  bankName: '',
  bankNameEng: null,
  isActive: true,
  creditRiskTypeId: null,
  counterpartyCreditRiskTypeId: null,
  isForeign: false,
  lendingFeeCalcTypeId: null,
});

export function BanksPage() {
  // The bank's own credit risk + the counterparty ("צד ג'") credit risk both
  // reference CreditRiskTypes — load that lookup once for both dropdowns.
  const { data: creditRiskTypes, loading, error } = useCreditRiskTypes();
  const {
    data: lendingFeeCalcTypes,
    loading: lendingFeeCalcTypesLoading,
    error: lendingFeeCalcTypesError,
  } = useLendingFeeCalcTypes();

  const columnDefs = useMemo<ColDef<Bank>[]>(() => {
    if (!creditRiskTypes || !lendingFeeCalcTypes) return [];

    const noneLabel = t('banks.noneOption');
    const values = [
      noneLabel,
      ...creditRiskTypes.map((c) => `${c.creditRiskTypeId} - ${c.creditRiskTypeName}`),
    ];
    const labelById = new Map<number, string>(
      creditRiskTypes.map((c) => [c.creditRiskTypeId, `${c.creditRiskTypeId} - ${c.creditRiskTypeName}`]),
    );
    const idByLabel = new Map<string, number>(
      creditRiskTypes.map((c) => [`${c.creditRiskTypeId} - ${c.creditRiskTypeName}`, c.creditRiskTypeId]),
    );

    // Lending-fee calc type: nullable FK, so the dropdown includes a "none" option.
    const lendingFeeCalcTypeValues = [
      noneLabel,
      ...lendingFeeCalcTypes.map(
        (l) => `${l.lendingFeeCalcTypeId} - ${l.lendingFeeCalcTypeName}`,
      ),
    ];
    const lendingFeeCalcTypeLabelById = new Map<number, string>(
      lendingFeeCalcTypes.map((l) => [
        l.lendingFeeCalcTypeId,
        `${l.lendingFeeCalcTypeId} - ${l.lendingFeeCalcTypeName}`,
      ]),
    );
    const lendingFeeCalcTypeIdByLabel = new Map<string, number>(
      lendingFeeCalcTypes.map((l) => [
        `${l.lendingFeeCalcTypeId} - ${l.lendingFeeCalcTypeName}`,
        l.lendingFeeCalcTypeId,
      ]),
    );

    const creditRiskCol = (field: CreditRiskField, headerName: string): ColDef<Bank> => ({
      field,
      headerName,
      width: 210,
      editable: true,
      cellDataType: 'text',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values },
      valueGetter: (p: ValueGetterParams<Bank>) => {
        const v = p.data?.[field];
        if (v == null) return noneLabel;
        return labelById.get(v) ?? String(v);
      },
      valueSetter: (p: ValueSetterParams<Bank>) => {
        if (!p.data) return false;
        const data = p.data as unknown as Record<string, number | null>;
        if (p.newValue === noneLabel) {
          data[field] = null;
          return true;
        }
        const id = idByLabel.get(p.newValue);
        if (id === undefined) return false;
        data[field] = id;
        return true;
      },
    });

    return [
      {
        field: 'bankId',
        headerName: t('banks.columnBankId'),
        width: 120,
        editable: (params: EditableCallbackParams<Bank>) =>
          (params.data as { __status?: string } | undefined)?.__status === 'added',
      },
      { field: 'bankName', headerName: t('banks.columnBankName'), flex: 1, editable: true },
      { field: 'bankNameEng', headerName: t('banks.columnBankNameEng'), flex: 1, editable: true },
      { field: 'isActive', headerName: t('banks.columnIsActive'), width: 100, editable: true, cellDataType: 'boolean' },
      creditRiskCol('creditRiskTypeId', t('banks.columnCreditRiskTypeId')),
      creditRiskCol('counterpartyCreditRiskTypeId', t('banks.columnCounterpartyCreditRiskTypeId')),
      { field: 'isForeign', headerName: t('banks.columnIsForeign'), width: 100, editable: true, cellDataType: 'boolean' },
      {
        field: 'lendingFeeCalcTypeId',
        headerName: t('banks.columns.lendingFeeCalcTypeId'),
        width: 240,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: lendingFeeCalcTypeValues },
        valueGetter: (p: ValueGetterParams<Bank>) => {
          if (!p.data || p.data.lendingFeeCalcTypeId == null) return noneLabel;
          return (
            lendingFeeCalcTypeLabelById.get(p.data.lendingFeeCalcTypeId) ??
            String(p.data.lendingFeeCalcTypeId)
          );
        },
        valueSetter: (p: ValueSetterParams<Bank>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.lendingFeeCalcTypeId = null;
            return true;
          }
          const newId = lendingFeeCalcTypeIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.lendingFeeCalcTypeId = newId;
          return true;
        },
      },
    ];
  }, [creditRiskTypes, lendingFeeCalcTypes]);

  const combinedError = error ?? lendingFeeCalcTypesError;
  if (combinedError) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'var(--color-error-text)', background: 'var(--color-error-bg)', padding: '8px 12px', borderRadius: 4 }}>
          {combinedError}
        </div>
      </div>
    );
  }

  if (
    loading ||
    lendingFeeCalcTypesLoading ||
    !creditRiskTypes ||
    !lendingFeeCalcTypes
  ) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <LopCrudPage<Bank>
      resourceCode="banks"
      titleKey="banks.title"
      idField="bankId"
      getRows={getBanks}
      saveBatch={(added, modified, deletedIds) =>
        saveBatchBanks(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newBankDefaults}
    />
  );
}
